
import {  Prop, Vue, Component, Watch } from "vue-property-decorator";
import SystemViewTreeNode from '@/types/sv-data/system-view/SystemViewTreeNode';
import api from "@/services/api.service";
import { Getter } from 'vuex-class';
import { UserPermissions, FeaturesList } from '@/store/types';
import { get, debounce } from 'lodash';
import  { SetupSummaryResponse, GroupSyncSummary } from '@/types/sv-data/system-view/SetupSummary';
import GroupSyncConfig from '../device-setup/sync/GroupSyncConfig.vue';
import { truncateString } from "@/filters";
import { groupSyncMixin } from "@/mixins";
import { SyncStateResponse } from "@/types/sv-data/devices/SyncSystem";
import { getDebouncePeriod } from "@/utils/debounce-helper";

const { groupSyncQueueable, convertQueueStateToFriendlyText } = groupSyncMixin.methods;

@Component({
	components: {
		"group-sync-config": GroupSyncConfig
	},
	filters: {
		truncateString
	}
})
export default class SetupOverviewGroupSync extends Vue {
	@Prop({ required: true, default: null }) private selectedNode: SystemViewTreeNode;

	@Getter getPermissions: UserPermissions;
	@Getter getFeaturesList: FeaturesList;

	private setupResponse: SetupSummaryResponse = null;

	// Given default values for Vue reactivity
	private groupSyncSummary: GroupSyncSummary = {
		groupSyncId: 0,
		syncSystemTitle: "",
		areaTitle: "",
		identifier: "",
		interval: 0,
		enabled: false,
		additionalConfig: "",
		lastRun: new Date(),
		nextRunDate: new Date(),
		lastQueueState: "",
		groupSyncTitle: "",
		syncSystemId: 0,
		groupId: 0,
	};

	private isPollingState: boolean = false;
	private statePoll: NodeJS.Timeout = null;
	private friendlyQueueState: string = "";
	private isQueueable: boolean = false;

	private pollGroupSyncInterval: number = 1000;

	private debouncedSyncNow: () => Promise<void>;

	private created(): void {
		this.debouncedSyncNow = debounce(this.syncNow, getDebouncePeriod());
	}

	private async mounted(): Promise<void> {
		await this.retrieveGroupSyncDetails();
		this.startSyncPoll();
	}

	private beforeDestroy(): void {
		this.clearPollTimeout();
		this.debouncedSyncNow = null;
	}

	@Watch("selectedNode", { deep: true })
	private async onSelectedNodeChange(): Promise<void> {
		if (this.selectedNode && this.selectedNode.objectTypeId && this.selectedNode.objectId) {
			this.clearPollTimeout();
			await this.retrieveGroupSyncDetails();
			this.startSyncPoll();
		}
	}

	private async retrieveGroupSyncDetails(resetGroupSummary: boolean = true): Promise<void> {
		// If no selected node or no system view feature, exit early
		if(!this.selectedNode || !this.canUseSystemView) {
			return;
		}

		// Reset previous data if specified
		if (resetGroupSummary) {
			this.groupSyncSummary = null; // Reset previous display data
			this.clearPollTimeout(); // Also clear any existing polls
		}

		this.setupResponse = await api.getSetupSummary(this.selectedNode.objectTypeId, this.selectedNode.objectId);

		if (this.setupResponse?.error) {
			this.groupSyncSummary = null; // Clear previous display data on error
			this.clearPollTimeout(); // Also clear any existing polls

			this.$notify({
				type: "danger",
				title: "Error Retrieving Group Sync Details",
			});
			return;
		}

		if (!this.setupResponse || !this.setupResponse.summary) {
			this.groupSyncSummary = null; // Clear previous display data if no summary
			this.clearPollTimeout(); // Also clear any existing polls

			this.$notify({
				type: "warning",
				title: "No Group Sync Configuration Found",
				text: "The selected node does not have a group sync configuration."
			});
			return;
		}

		// Attempt to parse the summary JSON
		let summary: GroupSyncSummary | null = null;
		try {
			summary = JSON.parse(this.setupResponse.summary) as GroupSyncSummary
		}
		catch(e) {
			console.error("Failed to Parse server summary: "+ e)
			this.setupResponse.error = "Failed to Parse server summary";
		}

		// We add this condition for polling state, 
		// to ensure that if user changes node while waiting for API response,
		// we do not process the response for the previous node
		if (summary.groupSyncId !== this.selectedNode.objectId) {
			// The response does not match the current selected node, ignore it
			return;
		}
		this.groupSyncSummary = summary;
	}

	private startSyncPoll(): void {
		if (this.isPerformSyncFeatureEnabled && this.groupSyncSummary) {
			this.friendlyQueueState = convertQueueStateToFriendlyText(this.groupSyncSummary.lastQueueState);
			this.isQueueable = groupSyncQueueable(this.groupSyncSummary.lastQueueState);

			if (this.groupSyncSummary
				&& this.groupSyncSummary.lastQueueState
				&& !this.isQueueable) {
				this.isPollingState = true;
				this.pollGroupSyncState();
			}
		}
	}

	private clearPollTimeout(): void {
		clearTimeout(this.statePoll);
		this.statePoll = null;

		this.isPollingState = false;
	}

	private get isPerformSyncFeatureEnabled(): boolean {
		return get(this.getFeaturesList, ["SystemView", "PerformSync"]);
	}

	private get canQueueGroupSync(): boolean {
		return this.getPermissions.canPerformSyncInStatus && this.isPerformSyncFeatureEnabled;
	}

	private get canUseSystemView(): boolean {
        return !!get(this.getFeaturesList, ["SystemView"]) && (this.getPermissions.canViewSystemView || this.getPermissions.isSystemAdmin);
	}

	private async pollGroupSyncState(): Promise<void>{
        if (!this.isPollingState){
            this.clearPollTimeout();
            return;
        }

		let currentState: SyncStateResponse = await api.RetrieveLatestQueueStateForGroupSync(this.groupSyncSummary.groupSyncId);

		this.friendlyQueueState = convertQueueStateToFriendlyText(currentState.state);
		this.isQueueable = groupSyncQueueable(currentState.state);

        if(!groupSyncQueueable(currentState.state) && this.isPollingState) {
			await this.retrieveGroupSyncDetails(false); // false param -> Refresh details without resetting polling state

			this.statePoll = setTimeout(() => { this.pollGroupSyncState() }, this.pollGroupSyncInterval);
			return;
        }

		this.friendlyQueueState = convertQueueStateToFriendlyText(currentState.state);
		this.isQueueable = groupSyncQueueable(currentState.state);
		this.clearPollTimeout();
	}

	private get syncButtonTooltip(): string { 
		if (!this.isPerformSyncFeatureEnabled) {
			return "";
		}

		if (!this.canQueueGroupSync) {
			return "You do not have permission to queue group syncs.";
		}

		if (!this.isQueueable) {
			return "Group sync is currently processing. Please wait until it completes before queuing another sync.";
		}

		return "Click to queue a group sync now.";
	}

	private async syncNow(): Promise<void> {
		if (this.canQueueGroupSync && this.groupSyncSummary) {
			await api.queueGroupSync(this.groupSyncSummary.groupSyncId);
			this.isPollingState = true;
			this.pollGroupSyncState();
		}
	}
}

